// src/components/invoices/InvoiceBuilder.jsx - Pure shadcn implementation
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { ArrowLeft, FileText, Send, Save, Eye } from 'lucide-react';

// Import shadcn components
import { Button } from "../ui/button";
import { Alert, AlertDescription } from "../ui/alert";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";

// Import our form component
import InvoiceForm from './forms/InvoiceForm';
import PageContainer from '../ui/layout/PageContainer/PageContainer';

// Import hooks
import { useInvoices } from '../../hooks/useInvoices';
import { useCustomers } from '../../hooks/useCustomers';
import { useEvents } from '../../hooks/useEvents';
import { useToast } from "../../hooks/use-toast";
import { useSocket } from '../../contexts/SocketContext';

const InvoiceBuilder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { invoiceId } = useParams();
  const { toast } = useToast();

  // Determine if we're editing
  const isEditing = !!invoiceId;
  const initialCustomerId = location.state?.customerId;
  const preSelectedAppointments = location.state?.appointmentIds || [];

  // Get data from hooks
  const {
    selectedInvoice,
    loading: invoicesLoading,
    createInvoice,
    updateInvoice,
    loadInvoiceDetails,
    sendInvoice,
    clearSelectedInvoice,
    creatingInvoice,
    savingInvoice,
    sendingInvoice,
    isConnected
  } = useInvoices();

  const {
    customers,
    loading: customersLoading,
    loadCustomers
  } = useCustomers();

  const {
    getCustomerEvents
  } = useEvents();

  // Socket for credit operations
  const socket = useSocket();

  // Local state
  const [availableCredits, setAvailableCredits] = useState([]);
  const [selectedCustomerForCredits, setSelectedCustomerForCredits] = useState(null);
  const [pendingCreditId, setPendingCreditId] = useState(null);

  // Canonical: creating/saving flag watcher — navigate on server confirmation
  const prevCreatingInvoice = useRef(false);
  const prevSavingInvoice = useRef(false);
  useEffect(() => {
    if (prevCreatingInvoice.current && !creatingInvoice) {
      navigate('/invoices');
    }
    prevCreatingInvoice.current = creatingInvoice;
  }, [creatingInvoice, navigate]);

  useEffect(() => {
    if (prevSavingInvoice.current && !savingInvoice) {
      navigate('/invoices');
    }
    prevSavingInvoice.current = savingInvoice;
  }, [savingInvoice, navigate]);

  // Load customers on mount
  useEffect(() => {
    if (isConnected && customers.length === 0 && !customersLoading) {
      loadCustomers();
    }
  }, [isConnected, customers.length, customersLoading, loadCustomers]);

  // Load invoice details if editing
  useEffect(() => {
    if (isEditing && invoiceId && !selectedInvoice && isConnected) {
      loadInvoiceDetails(invoiceId);
    }
  }, [isEditing, invoiceId, selectedInvoice, isConnected, loadInvoiceDetails]);

  // Clear selected invoice on unmount
  useEffect(() => {
    return () => {
      if (isEditing) {
        clearSelectedInvoice();
      }
    };
  }, [isEditing, clearSelectedInvoice]);

  // Listen for customer credits response
  useEffect(() => {
    if (!socket) return;

    const handleCreditsLoaded = (response) => {
      console.log('🔍 CREDITS DEBUG: handleCreditsLoaded fired, response:', JSON.stringify(response));
      if (response.status === 'success') {
        console.log('🔍 CREDITS DEBUG: setting credits:', response.data?.credits);
        setAvailableCredits(response.data?.credits || []);
      } else {
        console.log('🔍 CREDITS DEBUG: response.status was not success:', response.status);
      }
    };

    const handleCreditApplied = (response) => {
      if (response.status === 'success') {
        toast({
          title: "Credit Applied",
          description: `Credit applied to invoice successfully`,
        });
        setPendingCreditId(null);
      }
    };

    console.log('🔍 CREDITS DEBUG: registering listener for customer_credits_loaded');
    socket.on('customer_credits_loaded', handleCreditsLoaded);
    socket.on('credit_applied_to_invoice', handleCreditApplied);

    return () => {
      socket.off('customer_credits_loaded', handleCreditsLoaded);
      socket.off('credit_applied_to_invoice', handleCreditApplied);
    };
  }, [socket, toast]);

  // Fetch credits when customer is selected in the form
  const handleCustomerChange = (customerId) => {
    console.log('🔍 CREDITS DEBUG: handleCustomerChange called, customerId:', customerId, 'socket:', !!socket);
    if (customerId && socket) {
      setSelectedCustomerForCredits(customerId);
      socket.emit('get_customer_credits', { customer_id: customerId });
      console.log('🔍 CREDITS DEBUG: emitted get_customer_credits');
    } else {
      setAvailableCredits([]);
    }
  };

  // Load credits for initial customer
  useEffect(() => {
    if (initialCustomerId && socket && isConnected) {
      handleCustomerChange(initialCustomerId);
    }
  }, [initialCustomerId, socket, isConnected]);

  // Navigation handlers
  const handleBack = () => {
    navigate('/invoices');
  };

  // Form submission handler
  const handleSubmit = (formData) => {
    // Store credit_id if selected for later application
    if (formData.credit_id) {
      setPendingCreditId(formData.credit_id);
    }

    // Remove credit_id from invoice data (it's applied separately)
    const { credit_id, ...invoiceData } = formData;

    if (isEditing) {
      updateInvoice(invoiceId, invoiceData);
      // Apply credit if selected
      if (credit_id && socket) {
        socket.emit('apply_credit_to_invoice', {
          invoice_id: invoiceId,
          credit_id: credit_id
        });
      }
    } else {
      createInvoice(invoiceData);
    }
    // Navigation handled by flag watchers
  };

  // Send invoice handler
  const handleSend = () => {
    if (!selectedInvoice && !isEditing) {
      toast({
        title: "Error",
        description: "Please save the invoice before sending",
        variant: "destructive",
      });
      return;
    }
    if (sendingInvoice) return;
    sendInvoice(invoiceId);
  };

  // Preview handler
  const handlePreview = () => {
    // TODO: Open preview modal or navigate to preview page
    toast({
      title: "Preview",
      description: "Invoice preview feature coming soon!",
    });
  };

  // Connection error
  if (!isConnected) {
    return (
      <PageContainer>
        <Alert variant="destructive">
          <AlertDescription>
            Not connected to server. Please check your connection and try again.
          </AlertDescription>
        </Alert>
      </PageContainer>
    );
  }

  // Loading state for initial load
  if ((isEditing && invoicesLoading && !selectedInvoice) || (customersLoading && customers.length === 0)) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading invoice...</p>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" onClick={handleBack} className="p-2">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {isEditing ? 'Edit Invoice' : 'Create Invoice'}
            </h1>
            <p className="text-muted-foreground">
              {isEditing
                ? `Invoice #${selectedInvoice?.invoice_number || invoiceId}`
                : 'Create a new invoice for your customer'
              }
            </p>
          </div>
        </div>

        {/* Action buttons for editing mode */}
        {isEditing && selectedInvoice && (
          <div className="flex space-x-2">
            <Button variant="outline" onClick={handlePreview}>
              <Eye className="h-4 w-4 mr-2" />
              Preview
            </Button>
            <Button variant="default" onClick={handleSend} disabled={sendingInvoice}>
              <Send className="h-4 w-4 mr-2" />
              {sendingInvoice ? 'Sending...' : 'Send Invoice'}
            </Button>
          </div>
        )}
      </div>

      {/* Main Form */}
      <div className="max-w-4xl mx-auto">
        <InvoiceForm
          invoice={selectedInvoice}
          customers={customers}
          onSubmit={handleSubmit}
          onCancel={handleBack}
          loading={isEditing ? savingInvoice : creatingInvoice}
          isEditing={isEditing}
          initialCustomerId={initialCustomerId}
          preSelectedAppointments={preSelectedAppointments}
          getCustomerEvents={getCustomerEvents}
          availableCredits={availableCredits}
          onCustomerChange={handleCustomerChange}
        />
      </div>
    </PageContainer>
  );
};

export default InvoiceBuilder;